import React, { useEffect } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import validationSchema from './schema';
import Grid from '@mui/material/Grid';
import {
  CREATE_INVOICE,
  DOWNLOAD_INVOICE,
  GET_ALL_INVOICES,
  GET_INVOICE_BY_ID,
  UPDATE_INVOICE,
} from '../../../gql/invoices';
import Button from '@mui/material/Button';
import useSnackbar from '../../../hooks/useSnackbar';
import { getTenantId } from '../../../services/tenancy';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import { formatToTimeZone } from 'date-fns-timezone';
import usePermissions from '../../../hooks/usePermissions';
import {
  StyledTableCell,
  StyledTableHeaderCell,
  StyledTableRow,
  StyledTable,
  StyledTableContainer,
} from './styles';
import Paper from '@mui/material/Paper';

const timezone = { timeZone: 'UTC' };
const toBRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
interface InvoiceFormProps {
  uid?: string;
}
// @ts-ignore
const InvoiceForm: React.FC<InvoiceFormProps> = ({ uid }) => {
  const { state } = useLocation();
  let { id } = useParams();
  id = uid ?? id;
  const isCreate = id === 'create';
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const [mutateInvoice] = useMutation(isCreate ? CREATE_INVOICE : UPDATE_INVOICE, {
    refetchQueries: [{ query: GET_ALL_INVOICES }],
  });
  const [getCurrentInvoice, { data }] = useLazyQuery(GET_INVOICE_BY_ID, {
    fetchPolicy: 'no-cache',
  });
  const inv = useQuery(DOWNLOAD_INVOICE, { fetchPolicy: 'no-cache', variables: { id } });
  const { isAdmin, isOwner } = usePermissions();

  const navigateBack = () => navigate('/dashboard/invoices');

  const downloadINV = async () => {
    // @ts-ignore
    // eslint-disable-next-line valid-typeof
    const str = state !== null ? state.pdf : inv.data.downloadInvoice;
    const link = `data:application/pdf;base64,${str}`;
    const downloadLink = document.createElement('a');
    const fileName = `Pedido_de_venda_${formik.values.saleId}.pdf`;
    downloadLink.href = link;
    downloadLink.download = fileName;
    downloadLink.click();
  };
  const handleSubmit = (values: Record<any, any>) => {
    snackbar.setGlobalLoading(true);
    snackbar.sendAlert({ level: 'info', message: 'Salvando Dados...' });

    const input: Record<string, any> = { tenantId: getTenantId() };
    Object.keys(validationSchema.fields).map((key) => {
      if (Object.hasOwn(values, key)) input[key] = values[key];
      return null;
    });
    mutateInvoice({ variables: { input } })
      .then(() =>
        snackbar.sendAlert({ level: 'success', message: 'Dados salvos com sucesso ' })
      )
      .catch((er) => {
        snackbar.sendAlert({ level: 'error', message: 'Erro Salvando dados' });
        console.error(er);
      })
      .finally(() => {
        snackbar.setGlobalLoading(false);
        navigateBack();
      });
  };
  const formik = useFormik({
    // eslint-disable-next-line no-nested-ternary
    initialValues: data?.getInvoiceById
      ? {
          ...data.getInvoiceById,
          invoiceId: data.getInvoiceById?.invoice?.id,
        }
      : typeof state === 'object'
      ? {
          ...state,
          // @ts-ignore
          invoiceId: state?.id,
        }
      : {},
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  useEffect(() => {
    if (!isCreate) getCurrentInvoice({ variables: { id } });
  }, [isCreate]);

  const invoiceData = formik.values?.immutableData || {};

  const hasPublicTaxes =
    invoiceData.taxes?.length >= 1 &&
    invoiceData.taxes?.some((tax: any) => tax?.isPublic);

  const calculateNetPrice = (grossPrice: number): number => {
    if (!hasPublicTaxes) return grossPrice;
    const publicTaxes = invoiceData.taxes?.filter((tax: any) => tax.isPublic === true);
    if (!publicTaxes || !publicTaxes.some((tax: any) => tax?.isPublic)) return grossPrice;

    const taxAmount = publicTaxes
      .filter((tax: any) => tax.isPublic === true)
      .reduce((totalTax: number, tax: any) => {
        if (tax.type === 'percentage') {
          const taxPercentage = tax.value / (100 + tax.value);
          return totalTax + grossPrice * taxPercentage;
        }
        if (tax.type === 'fixed') {
          return totalTax + tax.value;
        }
        return totalTax;
      }, 0);

    return grossPrice - taxAmount;
  };

  const calculateTotalTaxes = (): number => {
    if (!hasPublicTaxes) return 0;

    return (
      formik.values?.immutableData?.regions.reduce(
        (totalTaxes: number, region: any) =>
          totalTaxes +
          region.items.reduce((regionTaxes: number, item: any) => {
            const grossPrice = item.unitPrice * item.qtd;
            const netPrice = calculateNetPrice(item.unitPrice) * item.qtd;
            return regionTaxes + (grossPrice - netPrice);
          }, 0),
        0
      ) || 0
    );
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container spacing={0} sx={{ mt: 2, border: '0.3px solid #e0e0e0' }}>
        <Grid item xs={12} sx={{ borderBottom: '0.3px solid #e0e0e0', p: 1 }}>
          <Grid container>
            <Grid item xs={9}>
              <Typography variant="h6">
                Pedido de venda Nº {formik.values.saleId}
              </Typography>
            </Grid>
            <Grid item xs={3} sx={{ textAlign: 'right' }}>
              <Typography variant="caption">
                Chácara Santo Antonio, São Paulo - SP
              </Typography>
              <Typography variant="caption" display="block">
                04.715-005
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid container>
          <Grid item xs={9}>
            <Table size="small" component={StyledTable}>
              <colgroup>
                <col style={{ width: '15%' }} />
                <col style={{ width: '85%' }} />
              </colgroup>
              <TableHead>
                <StyledTableRow>
                  <StyledTableHeaderCell colSpan={2}>
                    Informações do Cliente
                  </StyledTableHeaderCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                <StyledTableRow>
                  <StyledTableCell
                    component="th"
                    sx={{ backgroundColor: '#fff', fontWeight: 'bold' }}
                  >
                    Cliente
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography>
                      <strong>Razão social:</strong>{' '}
                      {formik.values.immutableData?.client?.companyName}
                    </Typography>
                    <Typography>
                      <strong>Nome fantasia:</strong>{' '}
                      {formik.values.immutableData?.client?.tradingName}
                    </Typography>
                    <Typography>
                      <strong>CNPJ:</strong> {formik.values.immutableData?.client?.cnpj}
                    </Typography>
                  </StyledTableCell>
                </StyledTableRow>
                <StyledTableRow>
                  <StyledTableCell
                    component="th"
                    sx={{ backgroundColor: '#fff', fontWeight: 'bold' }}
                  >
                    Endereço
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography>
                      {`${formik.values.immutableData?.companyAddress?.street}, ${
                        formik.values.immutableData?.companyAddress?.number
                      }${
                        formik.values.immutableData?.companyAddress?.complement
                          ? `, ${formik.values.immutableData?.companyAddress?.complement}`
                          : ''
                      } - ${formik.values.immutableData?.companyAddress?.district}`}
                    </Typography>
                    <Typography>
                      {`${formik.values.immutableData?.companyAddress?.city}, ${formik.values.immutableData?.companyAddress?.state}`}
                    </Typography>
                  </StyledTableCell>
                </StyledTableRow>
                <StyledTableRow>
                  <StyledTableCell
                    component="th"
                    sx={{ backgroundColor: '#fff', fontWeight: 'bold' }}
                  >
                    Contato
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography>
                      Fone/Cel: {formik.values.immutableData?.contact?.telephone}
                    </Typography>
                    <Typography>
                      Celular: {formik.values.immutableData?.contact?.contactNumber}
                    </Typography>
                    <Typography>
                      Email: {formik.values.immutableData?.contact?.contactEmail}
                    </Typography>
                  </StyledTableCell>
                </StyledTableRow>
                <StyledTableRow>
                  <StyledTableCell
                    component="th"
                    sx={{ backgroundColor: '#fff', fontWeight: 'bold' }}
                  >
                    Gerente
                  </StyledTableCell>
                  <StyledTableCell>
                    {formik.values.contract?.seller?.firstName}{' '}
                    {formik.values.contract?.seller?.lastName}
                  </StyledTableCell>
                </StyledTableRow>
              </TableBody>
            </Table>
          </Grid>

          <Grid item xs={3}>
            <Table size="small" component={StyledTable}>
              <colgroup>
                <col style={{ width: '40%' }} />
                <col style={{ width: '60%' }} />
              </colgroup>
              <TableHead>
                <StyledTableRow>
                  <StyledTableHeaderCell colSpan={2}>NF-e</StyledTableHeaderCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                <StyledTableRow>
                  <StyledTableCell
                    component="th"
                    sx={{ backgroundColor: '#fff', fontWeight: 'bold' }}
                  >
                    Pedido de Venda
                  </StyledTableCell>
                  <StyledTableCell>{formik.values.saleId || '---'}</StyledTableCell>
                </StyledTableRow>
                <StyledTableRow>
                  <StyledTableCell
                    component="th"
                    sx={{ backgroundColor: '#fff', fontWeight: 'bold' }}
                  >
                    Emissão do PV
                  </StyledTableCell>
                  <StyledTableCell>
                    {formik.values.createdAt
                      ? formatToTimeZone(
                          new Date(Number(formik.values.createdAt)),
                          'DD/MM/YYYY',
                          timezone
                        )
                      : '---'}
                  </StyledTableCell>
                </StyledTableRow>
                <StyledTableRow>
                  <StyledTableCell
                    component="th"
                    sx={{ backgroundColor: '#fff', fontWeight: 'bold' }}
                  >
                    Nota Fiscal Eletron.
                  </StyledTableCell>
                  <StyledTableCell>{formik.values.nfeNumber || '---'}</StyledTableCell>
                </StyledTableRow>
                <StyledTableRow>
                  <StyledTableCell
                    component="th"
                    sx={{ backgroundColor: '#fff', fontWeight: 'bold' }}
                  >
                    Emissão da NF-e
                  </StyledTableCell>
                  <StyledTableCell>
                    {formik.values.nfeDate
                      ? formatToTimeZone(
                          new Date(Number(formik.values.nfeDate)),
                          'DD/MM/YYYY',
                          timezone
                        )
                      : '---'}
                  </StyledTableCell>
                </StyledTableRow>
              </TableBody>
            </Table>
          </Grid>
        </Grid>
      </Grid>
      <Grid container sx={{ mt: 4 }}>
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Relatório de consumo
          </Typography>
        </Grid>
        {(formik.values?.immutableData?.regions ?? []).map((region: any) => (
          <Grid item xs={12} key={region.namespace}>
            <StyledTableContainer>
              <Table size="small" component={StyledTable}>
                <colgroup>
                  <col style={{ width: '30%' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '20%' }} />
                </colgroup>
                <TableHead>
                  <StyledTableRow>
                    <StyledTableHeaderCell colSpan={5}>
                      {region.namespace}
                    </StyledTableHeaderCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell sx={{ fontWeight: 'bold', textAlign: 'left' }}>
                      Item
                    </StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 'bold', textAlign: 'left' }}>
                      Cod.(SKU)
                    </StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 'bold', textAlign: 'left' }}>
                      Qtd
                    </StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>
                      Preço Un.(R$)
                    </StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>
                      Total (R$)
                    </StyledTableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody>
                  {(region.items ?? []).map((row: any, idx: number) => (
                    <StyledTableRow key={row.sku + region.namespace + idx}>
                      <StyledTableCell sx={{ textAlign: 'left' }}>
                        {row.item}
                      </StyledTableCell>
                      <StyledTableCell sx={{ textAlign: 'left' }}>
                        {row.sku}
                      </StyledTableCell>
                      <StyledTableCell sx={{ textAlign: 'left' }}>
                        {row.qtd}
                      </StyledTableCell>
                      <StyledTableCell sx={{ textAlign: 'right' }}>
                        {toBRL.format(calculateNetPrice(row.unitPrice))}
                      </StyledTableCell>
                      <StyledTableCell sx={{ textAlign: 'right' }}>
                        {toBRL.format(calculateNetPrice(row.unitPrice) * row.qtd)}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
            Observações
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: '#f8f9fa',
                  border: '0.3px solid #e0e0e0',
                  borderRadius: 1,
                  height: '100%',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Tier
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {formik.values.immutableData?.productTier || '-'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: '#f8f9fa',
                  border: '0.3px solid #e0e0e0',
                  borderRadius: 1,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Dólar
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {formik.values.immutableData?.dollarRate?.toFixed(4) || '-'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formik.values.immutableData?.dollarType || '-'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: '#f8f9fa',
                      border: '0.3px solid #e0e0e0',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      SubTotal
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {toBRL.format(
                        formik.values?.immutableData?.regions.reduce(
                          (subtotal: number, region: any) =>
                            subtotal +
                            region.items.reduce(
                              (itemSubtotal: number, item: any) =>
                                itemSubtotal +
                                calculateNetPrice(item.unitPrice) * item.qtd,
                              0
                            ),
                          0
                        )
                      )}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={hasPublicTaxes ? 3 : 4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: '#f8f9fa',
                      border: '0.3px solid #e0e0e0',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Descontos
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 'bold', color: '#000000' }}
                    >
                      -{' '}
                      {toBRL.format(
                        formik.values?.immutableData?.credits.reduce(
                          (pv: number, cv: any) => pv + Number(cv.value),
                          0
                        )
                      )}
                    </Typography>
                  </Paper>
                </Grid>
                {hasPublicTaxes && (
                  <Grid item xs={3}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: '#f8f9fa',
                        border: '0.3px solid #e0e0e0',
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Acrécimos
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 'bold', color: '#d32f2f' }}
                      >
                        - {toBRL.format(calculateTotalTaxes())}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                <Grid item xs={hasPublicTaxes ? 3 : 4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: '#f5f5f5',
                      border: '0.3px solid #e0e0e0',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Total
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 'bold', color: '#00c140ae' }}
                    >
                      {toBRL.format(formik.values.totalValue)}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 4 }} justifyContent="flex-end">
        <Grid item>
          <Button size="large" onClick={navigateBack}>
            Voltar
          </Button>
        </Grid>
        {(isOwner(formik.values.contract?.sellerId) || isAdmin) && (
          <Grid item>
            <Button size="large" onClick={downloadINV} variant="contained">
              Download Pedido
            </Button>
          </Grid>
        )}
      </Grid>
    </form>
  );
};

export default InvoiceForm;
